import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Field,
  Flex,
  Heading,
  Separator,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { IconArrowLeft, IconPen } from '@optimizely/axiom-icons';
import { Edge, Node } from '@xyflow/react';

import { workflowAgentAtom } from '@frontend/atoms';
import { Portal, Sidebar } from '@frontend/components';
import { ConfirmationModal } from '@frontend/components/confirmationModal/components/ConfirmationModal';
import { HYPATIA_URL } from '@frontend/envConfig';
import { useInstanceId, useNamespacedNavigate, useObjectTitle } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles';
import * as agentStyles from '@frontend/styles/form.css';
import { WorkflowAgent } from '@frontend/types/workflow-agent';

import {
  useAgentId,
  useCreateWorkflowAgent,
  useDeleteWorkflowAgent,
  useGetWorkflowAgentWithGraph,
  useUpdateWorkflowAgent,
} from '../../../hooks';
import { AgentExecutionsTab } from '../../executions/AgentExecutionsTab';
import { AgentActionButtons } from '../../shared/AgentActionButtons';
import { AgentBasicFields } from '../../shared/AgentBasicFields';
import { nodeConfigs } from '../nodes/configs';
import { WorkflowProvider } from '../providers/WorkflowProvider';
import { WorkflowBuilder } from '../workflow-builder/WorkflowBuilder';
import { WorkflowPreviewCanvas } from '../workflow-builder/WorkflowPreviewCanvas';

import { WorkflowAgentExecutionForm } from './WorkflowAgentExecutionForm';
import { styles } from './WorkflowAgentForm.css';

import '@xyflow/react/dist/style.css';

export function WorkFlowAgentForm({ backHref }: { backHref?: string | null } = {}) {
  const navigate = useNamespacedNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { instanceId } = useInstanceId();

  // Initialize workflow builder state from query params
  const initialBuilderState = searchParams.get('workflowBuilder') === 'true';
  const [isWorkflowBuilderOpen, setIsWorkflowBuilderOpen] = useState(initialBuilderState);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const currentHref = location.pathname + location.search + location.hash;

  // Copy mode detection
  const isCopyMode = location.pathname.includes('/copy');
  // isEditing should be true if we have an id from URL OR if the form data has an id (after auto-save), but not in copy mode
  const [isEditing, setIsEditing] = useState((id && !isCopyMode) || false);

  const activeTab = useMemo((): string => {
    const tabFromQuery = searchParams.get('tab');
    switch (tabFromQuery) {
      case 'logs':
        return 'logs';
      default:
        return 'setup';
    }
  }, [searchParams]);

  const [formData, dispatch] = useAtom(workflowAgentAtom);

  // Set document title for the agent
  useObjectTitle(formData.name);

  const goBackToBackHref = useCallback(() => {
    navigate(backHref ? backHref : '/agents');
  }, [backHref, navigate]);

  // Use id from URL params or from formData (after auto-save)
  const workflowId = id || formData.id;
  const agentUrl =
    (isEditing || isCopyMode) && workflowId && instanceId
      ? `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${workflowId}`
      : '';

  const { agent: fetchedAgent, isPending: isAgentLoading } = useGetWorkflowAgentWithGraph(
    agentUrl,
    !!(workflowId && (isEditing || isCopyMode))
  );

  useEffect(() => {
    if (fetchedAgent && isCopyMode) {
      const filteredNodes =
        (fetchedAgent.nodes as unknown[])?.filter(
          (node: unknown) => (node as Node).type !== 'trigger'
        ) || [];

      // In copy mode, transform the data for new agent creation
      const copiedData: WorkflowAgent = {
        ...fetchedAgent,
        id: '', // Clear ID to ensure new agent creation
        name: `${fetchedAgent.name} copy`, // Suffix name
        agent_id: '', // Clear agent_id for new creation
        created_at: '',
        updated_at: '',
        triggers: [], // Empty triggers list for copy
        nodes: filteredNodes as WorkflowAgent['nodes'],
        // Clear any other fields that should be reset for copy
      };
      dispatch({ type: 'SET_ALL', payload: copiedData });
    } else if (fetchedAgent && isEditing) {
      // In edit mode, use the original data
      dispatch({ type: 'SET_ALL', payload: fetchedAgent });
    }
  }, [fetchedAgent, isEditing, isCopyMode, dispatch]);

  // Use agent ID availability checking
  const {
    isCheckingAvailability,
    availabilityStatus,
    idFieldStatus,
    isSaveDisabled: isAgentIdSaveDisabled,
  } = useAgentId(instanceId, formData.agent_id || '', isEditing, fetchedAgent?.agent_id);

  const isRequiredFieldsEmpty = !formData.name?.trim() || !formData.description?.trim();
  const isSaveDisabled = isRequiredFieldsEmpty || isAgentIdSaveDisabled;

  // Handler for agent basic fields changes
  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      dispatch({
        type: 'UPDATE_FIELD',
        field: field as keyof WorkflowAgent,
        value: value as WorkflowAgent[keyof WorkflowAgent],
      });
      // Clear validation errors when user starts fixing the fields
      if (showValidationErrors) {
        setShowValidationErrors(false);
      }
    },
    [dispatch, showValidationErrors]
  );

  // Handler for agent ID suggestion clicks
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      dispatch({ type: 'UPDATE_FIELD', field: 'agent_id', value: suggestion });
    },
    [dispatch]
  );

  const { mutate: createWorkflowAgent } = useCreateWorkflowAgent(() => {
    setIsEditing(true);
    goBackToBackHref();
  });

  const { mutate: createEmptyWorkflowAgent } = useCreateWorkflowAgent(
    (savedAgent: WorkflowAgent) => {
      setIsEditing(true);
      // Add builder query param when opening
      const newPath = `/agents/workflow/edit/${savedAgent.id}?workflowBuilder=true`;
      setIsWorkflowBuilderOpen(true);
      navigate(newPath, { replace: true });
    }
  );

  const onClickEditWorkflow = () => {
    if (isRequiredFieldsEmpty) {
      setShowValidationErrors(true);
      return;
    }

    if (isEditing) {
      // Add builder query param when opening
      searchParams.set('workflowBuilder', 'true');
      setSearchParams(searchParams, { replace: true });
      setIsWorkflowBuilderOpen(true);
      return;
    }

    createEmptyWorkflowAgent(formData);
  };

  const handleCloseWorkflowBuilder = useCallback(() => {
    // Remove builder query param when closing
    searchParams.delete('workflowBuilder');
    setSearchParams(searchParams, { replace: true });
    setIsWorkflowBuilderOpen(false);
  }, [searchParams, setSearchParams]);

  const { mutate: updateWorkflowAgent } = useUpdateWorkflowAgent();
  const { mutate: deleteWorkflowAgent } = useDeleteWorkflowAgent(() => {
    goBackToBackHref();
  });

  const handleSave = useCallback(() => {
    if (isEditing && !isCopyMode && fetchedAgent?.links?.update) {
      updateWorkflowAgent({
        updatedAgent: formData,
        updateUrl: fetchedAgent?.links?.update,
      });
    } else {
      createWorkflowAgent(formData);
    }
  }, [
    fetchedAgent?.links?.update,
    updateWorkflowAgent,
    formData,
    createWorkflowAgent,
    isEditing,
    isCopyMode,
  ]);

  // Handle save with draft data from WorkflowBuilder
  const handleSaveWithDraftData = useCallback(
    (draftNodes: Node[], draftEdges: Edge[]) => {
      // Create updated form data with the draft nodes and edges
      const updatedFormData = {
        ...formData,
        nodes: draftNodes,
        edges: draftEdges,
      };

      // Save using the updated data directly
      if (isEditing && fetchedAgent?.links?.update) {
        updateWorkflowAgent({
          updatedAgent: updatedFormData,
          updateUrl: fetchedAgent.links.update,
        });
      } else {
        createWorkflowAgent(updatedFormData);
      }

      // After successful save, commit the draft data to the atom
      dispatch({ type: 'UPDATE_NODES_AND_EDGES', nodes: draftNodes, edges: draftEdges });
    },
    [
      formData,
      isEditing,
      fetchedAgent?.links?.update,
      updateWorkflowAgent,
      createWorkflowAgent,
      dispatch,
    ]
  );

  const hasChatTrigger = useMemo(() => {
    return formData.nodes?.some(node => node.type === 'message') ?? false;
  }, [formData.nodes]);

  if (isAgentLoading && (isEditing || isCopyMode)) {
    return (
      <Flex justifyContent="center" alignItems="center" w="full" h="full">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Flex className={AppPagePadding}>
      <Flex className={styles.stickyBackButton}>
        <Button
          appearance="subtle"
          icon={<IconArrowLeft size="12" />}
          onClick={goBackToBackHref}
          w="fit"
        >
          Agents
        </Button>
      </Flex>
      <Tabs
        value={activeTab}
        onValueChange={value => {
          if (value === 'setup') {
            searchParams.delete('tab');
          } else {
            searchParams.set('tab', value);
          }
          setSearchParams(searchParams, { replace: true });
        }}
        className={
          isSidebarOpen || isWorkflowBuilderOpen
            ? agentStyles.FormAgentMaxWidthSidebarOpen
            : agentStyles.FormAgentMaxWidth
        }
      >
        <Flex className={styles.stickyTabsListWithHeader} pt="8">
          <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
            <Heading level="3" color="fg.default" className={styles.titleHeader} truncate>
              {formData.name || 'Workflow Agent'}
            </Heading>

            <AgentActionButtons
              type="workflow"
              agentId={formData.id}
              isTestRunDisabled={!hasChatTrigger || isCopyMode}
              isEditing={isEditing && !isCopyMode}
              isSaveDisabled={isSaveDisabled}
              isCreating={false}
              isUpdating={false}
              onSave={handleSave}
              isSidebarOpen={isSidebarOpen || isWorkflowBuilderOpen}
              onTestRun={() => {
                setSidebarOpen(true);
              }}
              onDelete={() => setDeleteDialogOpen(true)}
            />
          </Flex>
          <TabsList>
            <TabsTrigger value="setup">Setup</TabsTrigger>
            {workflowId && <TabsTrigger value="logs">Logs</TabsTrigger>}
          </TabsList>
        </Flex>

        <TabsContent py="24" value="setup">
          <Flex flexDirection="column" gap="24">
            {fetchedAgent?.is_system_agent && (
              <Alert intent="information">
                This is an Optimizely-managed agent. Some fields are locked to ensure consistent
                performance.
              </Alert>
            )}
            <AgentBasicFields
              lockedFields={isCopyMode ? [] : fetchedAgent?.locked_fields || []}
              isInstalled={isCopyMode ? false : !!fetchedAgent?.is_installed}
              name={formData.name || ''}
              isActive={formData.is_active || false}
              agentId={formData.agent_id || ''}
              agentType="workflow"
              description={formData.description || ''}
              idFieldStatus={idFieldStatus}
              isCheckingAvailability={isCheckingAvailability}
              availabilityStatus={availabilityStatus}
              onChange={handleFieldChange}
              onSuggestionClick={handleSuggestionClick}
              showErrors={showValidationErrors}
            />
            <Flex flexDirection="column" gap="4">
              <Flex flexDirection="row" gap="16" justifyContent="space-between">
                <Field label="Workflow" required alignSelf="end" />
                <Tooltip content={isCopyMode ? 'Please save the agent first' : undefined}>
                  <Button
                    disabled={isCopyMode}
                    addonBefore={<IconPen size="16" />}
                    onClick={onClickEditWorkflow}
                  >
                    Edit Workflow
                  </Button>
                </Tooltip>
              </Flex>
              <WorkflowProvider nodeConfigs={nodeConfigs}>
                <WorkflowPreviewCanvas
                  nodes={formData.nodes || []}
                  edges={formData.edges || []}
                  showControls
                  className={styles.reactFlow}
                />
              </WorkflowProvider>
            </Flex>
            <Separator />
            <Text color="fg.error.hovered">* Required fields</Text>
          </Flex>
        </TabsContent>

        <TabsContent py="24" value="logs">
          <AgentExecutionsTab agentId={workflowId} backHref={currentHref} />
        </TabsContent>
      </Tabs>
      <Portal isOpen={isWorkflowBuilderOpen} onClose={handleCloseWorkflowBuilder}>
        <WorkflowProvider nodeConfigs={nodeConfigs}>
          <WorkflowBuilder
            onClose={handleCloseWorkflowBuilder}
            onSave={handleSaveWithDraftData}
            initialNodes={formData.nodes || []}
            initialEdges={formData.edges || []}
          />
        </WorkflowProvider>
      </Portal>
      <Sidebar open={isSidebarOpen} dock="right" initialWidth={450}>
        <WorkflowAgentExecutionForm
          agent={formData}
          hideRightSidebar={() => setSidebarOpen(false)}
        />
      </Sidebar>

      <ConfirmationModal
        isShown={deleteDialogOpen}
        title="Delete Agent"
        action="Delete"
        appearance="danger"
        onCancel={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          if (fetchedAgent?.links?.delete) {
            dispatch({ type: 'RESET' });
            deleteWorkflowAgent(fetchedAgent.links.delete);
          }
          setDeleteDialogOpen(false);
        }}
      >
        <Flex flexDirection="column" gap="4">
          <Text>
            You are about to permanently delete the agent{' '}
            <Text asChild fontWeight="600">
              <span>{formData.name}</span>
            </Text>
          </Text>
          <Text>Do you want to continue?</Text>
        </Flex>
      </ConfirmationModal>
    </Flex>
  );
}
